import { useState, useEffect, useCallback,useMemo } from 'react';
import { fetchWidgetData } from './WidgetDataFetcher';


export function useDashboardData({ config, enabled = true }: any) {
  const [rawData, setRawData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, any>>({});

  // Safely handle null/undefined config
  const sectionConfigs = useMemo(() => config || [], [config]);

  // Only create configKey if we have valid configs
  const configKey = useMemo(() => {
    if (!sectionConfigs || sectionConfigs.length === 0) return null;
    return JSON.stringify(sectionConfigs.map((c: any) => c.apiConfig));
  }, [sectionConfigs]);

  useEffect(() => {
    // Don't fetch if not enabled, no config, or empty config
    if (!enabled || !config ) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      setErrors({});
      try {
        const apiConfigs = sectionConfigs.map((c: any) => c.apiConfig);
        const settled = await Promise.allSettled(
          apiConfigs.map((apiConfig: any) => fetchWidgetData(apiConfig))
        );

        const results = settled.map((r: any) =>
          r.status === 'fulfilled' ? r.value : null
        );

        const nextErrors: Record<string, any> = {};
        settled.forEach((r: any, index: number) => {
          if (r.status === 'rejected') {
            const id = sectionConfigs[index]?.id ?? index;
            nextErrors[id] = r.reason;
          }
        });

        setRawData(results);
        setErrors(nextErrors);
        const firstRejected = settled.find((r: any) => r.status === 'rejected');
        setError(firstRejected ? (firstRejected as any).reason : null);
      } catch (e: any) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, enabled]);

  // Transform raw data for each widget
  const getWidgetData = useCallback(
    (widget: any) => {
      if (!rawData || rawData.length === 0) {
        return { chartData: [], metrics: null };
      }

      if (widget.dataMapping?.transform) {
        try {
          const transformedObj = widget.dataMapping.transform(rawData);
          if(!!transformedObj?.dynaDataKey){
              widget.dataKey = transformedObj.dynaDataKey
          }
          return {
            chartData: !transformedObj?.chartData ? transformedObj : transformedObj.chartData,
            dynaDataKey : transformedObj?.dynaDataKey,
            metrics: rawData,
          };
        } catch (e) {
          return { chartData: [], metrics: null };
        }
      }

      return { chartData: [], metrics: rawData };
    },
    [rawData]
  );

  const getWidgetError = useCallback(
    (widget: any) => {
      if (widget?.dataSourceId) {
        return errors[widget.dataSourceId] || null;
      }
      const keys = Object.keys(errors);
      return keys.length ? errors[keys[0]] : null;
    },
    [errors]
  );

  return { rawData, isLoading, error, errors, getWidgetData, getWidgetError };
}